import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { serverDomain } from "../../../config";
import { FaArrowDown, FaArrowUp } from "react-icons/fa";
import "../../../styles/ProjectIncomePage.css";

const ProjectIncomePage = () => {
  const navigate = useNavigate();

  const toNum = (v) => Number(v) || 0;

  const formatDateMSK = (date) => {
    const mskOffset = 3 * 60 * 60 * 1000;
    return new Date(date.getTime() + mskOffset).toISOString().split("T")[0];
  };

  const [statistics, setStatistics] = useState({});
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [interval, setInterval] = useState("по дням");
  const [groupBy, setGroupBy] = useState("none");

  const [sortColumn, setSortColumn] = useState("period");
  const [sortOrder, setSortOrder] = useState("desc");

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // init dates once
  useEffect(() => {
    const today = new Date();
    const lastWeek = new Date();
    lastWeek.setDate(today.getDate() - 7);
    setStartDate(formatDateMSK(lastWeek));
    setEndDate(formatDateMSK(today));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const groupStatistics = useCallback(
    (dataObj, groupByMode) => {
      if (!dataObj || typeof dataObj !== "object") return {};

      const grouped = {};

      Object.entries(dataObj).forEach(([key, value]) => {
        let groupKey = key.split(" | ")[0]; // период

        if (groupByMode === "offer") {
          groupKey += ` | ${value.offer_name || "Неизвестный оффер"}`;
        } else if (groupByMode === "stream") {
          groupKey += ` | ${key.split(" | ")[1] || "—"}`;
        } else if (groupByMode === "landing") {
          groupKey += ` | Fast Love RevShare`;
        }

        if (!grouped[groupKey]) {
          grouped[groupKey] = {
            clicks: 0,
            registrations: 0,
            purchases: 0,
            auto_debit: 0,
            income: 0,
            tg_stars: 0,
            net_purchases: 0,
            cr: 0,
            epc: 0,
          };
        }

        grouped[groupKey].clicks += toNum(value.clicks);
        grouped[groupKey].registrations += toNum(value.registrations);
        grouped[groupKey].purchases += toNum(value.purchases);
        grouped[groupKey].auto_debit += toNum(value.auto_debit);
        grouped[groupKey].income += toNum(value.income);
        grouped[groupKey].tg_stars += toNum(value.tg_stars);
      });

      // calc CR/EPC
      for (const k in grouped) {
        const s = grouped[k];
        s.net_purchases = toNum(s.purchases) - toNum(s.auto_debit);
        s.cr = s.registrations > 0 ? (s.net_purchases / s.registrations) * 100 : 0;
        s.epc = s.clicks > 0 ? s.income / s.clicks : 0;
      }

      return grouped;
    },
    [] // toNum is stable from closure (primitive fn)
  );

  const fetchStatistics = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
    if (!startDate || !endDate) return;

    setLoading(true);
    setErrorMessage("");

    try {
      const url = `${serverDomain}/api/statistics/project?start_date=${startDate}&end_date=${endDate}&interval=${interval}`;
      const response = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Ошибка при получении статистики проекта");

      const data = await response.json();
      if (!data || typeof data !== "object") throw new Error("Некорректный формат данных");

      setStatistics(groupStatistics(data, groupBy));
    } catch (e) {
      console.error(e);
      setStatistics({});
      setErrorMessage(e.message || "Ошибка");
    } finally {
      setLoading(false);
    }
  }, [navigate, startDate, endDate, interval, groupBy, groupStatistics]);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  const renderSortIcon = (column) => {
    if (column !== sortColumn) return null;
    return sortOrder === "asc" ? <FaArrowUp /> : <FaArrowDown />;
  };

  const parseKeyDate = (raw) => {
    if (!raw) return 0;
    // "DD.MM.YYYY"
    if (raw.includes(".")) {
      const [dd, mm, yy] = raw.split(".").map((x) => Number(x));
      const t = new Date(yy, (mm || 1) - 1, dd || 1).getTime();
      return Number.isFinite(t) ? t : 0;
    }
    const t = Date.parse(raw);
    return Number.isFinite(t) ? t : 0;
  };

  const sortedStatistics = useMemo(() => {
    return Object.entries(statistics).sort(([keyA, valueA], [keyB, valueB]) => {
      let valA, valB;

      if (sortColumn === "period") {
        valA = parseKeyDate((keyA.split(" | ")[0] || "").trim());
        valB = parseKeyDate((keyB.split(" | ")[0] || "").trim());
      } else if (sortColumn === "group") {
        valA = (keyA.split(" | ")[1] || "").toString();
        valB = (keyB.split(" | ")[1] || "").toString();
      } else {
        valA = Number(valueA[sortColumn]) || 0;
        valB = Number(valueB[sortColumn]) || 0;
      }

      if (sortOrder === "asc") return valA < valB ? -1 : valA > valB ? 1 : 0;
      return valA < valB ? 1 : valA > valB ? -1 : 0;
    });
  }, [statistics, sortColumn, sortOrder]);

  const totals = useMemo(() => {
    const vals = Object.values(statistics);

    const sum = vals.reduce(
      (acc, s) => {
        acc.clicks += toNum(s.clicks);
        acc.registrations += toNum(s.registrations);
        acc.auto_debit += toNum(s.auto_debit);
        acc.net_purchases += toNum(s.net_purchases ?? (toNum(s.purchases) - toNum(s.auto_debit)));
        acc.income += toNum(s.income);
        acc.tg_stars += toNum(s.tg_stars);
        return acc;
      },
      { clicks: 0, registrations: 0, auto_debit: 0, net_purchases: 0, income: 0, tg_stars: 0, cr: 0, epc: 0 }
    );

    sum.cr = sum.registrations > 0 ? (sum.net_purchases / sum.registrations) * 100 : 0;
    sum.epc = sum.clicks > 0 ? sum.income / sum.clicks : 0;
    return sum;
  }, [statistics]);

  return (
    <div className="fl-adminpage fl-projectIncome">
      <div className="fl-adminpage__head">
        <h2 className="fl-h2">Доход всего проекта</h2>
        <div className="fl-adminpage__meta">
          Строк: <b>{sortedStatistics.length}</b>
        </div>
      </div>

      <div className="fl-toolbar fl-projectIncome__toolbar">
        <input className="fl-input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        <span className="fl-toolbar__dash">-</span>
        <input className="fl-input" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />

        <select className="fl-select" value={interval} onChange={(e) => setInterval(e.target.value)}>
          <option value="по дням">по дням</option>
          <option value="по неделям">по неделям</option>
          <option value="по месяцам">по месяцам</option>
        </select>

        <select className="fl-select" value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
          <option value="none">Без группировки</option>
          <option value="offer">По офферу</option>
          <option value="stream">По потоку</option>
          <option value="landing">По лендингу</option>
        </select>

        <button className="fl-btn fl-btn--ghost" type="button" onClick={fetchStatistics} disabled={loading}>
          {loading ? "Загрузка…" : "Обновить"}
        </button>

        <div className="fl-projectIncome__totals">
          Доход: <b>{totals.income.toFixed(2)} ₽</b>
        </div>
      </div>

      {errorMessage ? <div className="fl-alert fl-alert--error">{errorMessage}</div> : null}

      {loading ? (
        <div className="loading-container">
          <div className="loader" />
        </div>
      ) : sortedStatistics.length === 0 ? (
        <div className="fl-empty">Нет данных за выбранный период</div>
      ) : (
        <div className="fl-table-wrap">
          <table className="fl-table fl-projectIncome__table">
            <thead>
              <tr>
                <th className="fl-sort" onClick={() => handleSort("period")}>
                  Период {renderSortIcon("period")}
                </th>

                {groupBy !== "none" && (
                  <th className="fl-sort col-group" onClick={() => handleSort("group")}>
                    Группировка {renderSortIcon("group")}
                  </th>
                )}

                <th className="fl-sort fl-num" onClick={() => handleSort("clicks")}>
                  Переходы {renderSortIcon("clicks")}
                </th>
                <th className="fl-sort fl-num" onClick={() => handleSort("cr")}>
                  CR {renderSortIcon("cr")}
                </th>
                <th className="fl-sort fl-num col-epc" onClick={() => handleSort("epc")}>
                  EPC {renderSortIcon("epc")}
                </th>
                <th className="fl-sort fl-num col-reg" onClick={() => handleSort("registrations")}>
                  Регистрации {renderSortIcon("registrations")}
                </th>
                <th className="fl-sort fl-num col-auto" onClick={() => handleSort("auto_debit")}>
                  Автосписания {renderSortIcon("auto_debit")}
                </th>
                <th className="fl-sort fl-num" onClick={() => handleSort("net_purchases")}>
                  Покупки {renderSortIcon("net_purchases")}
                </th>
                <th className="fl-sort fl-num col-stars" onClick={() => handleSort("tg_stars")}>
                  TG Starts {renderSortIcon("tg_stars")}
                </th>
                <th className="fl-sort fl-num" onClick={() => handleSort("income")}>
                  Доход {renderSortIcon("income")}
                </th>
              </tr>
            </thead>

            <tbody>
              {sortedStatistics.map(([key, value]) => (
                <tr key={key}>
                  <td>{key.split(" | ")[0]}</td>

                  {groupBy !== "none" && <td className="col-group">{key.split(" | ")[1]}</td>}

                  <td className="fl-num">{value.clicks}</td>
                  <td className="fl-num">{value.cr.toFixed(2)}%</td>
                  <td className="fl-num col-epc">{value.epc.toFixed(2)}</td>
                  <td className="fl-num col-reg">{value.registrations}</td>
                  <td className="fl-num col-auto">{value.auto_debit}</td>
                  <td className="fl-num">{value.net_purchases}</td>
                  <td className="fl-num col-stars">{value.tg_stars}</td>
                  <td className="fl-num">{value.income.toFixed(2)} ₽</td>
                </tr>
              ))}
            </tbody>

            <tfoot>
              <tr>
                <td>Итого</td>
                {groupBy !== "none" && <td className="col-group" />}
                <td className="fl-num">{totals.clicks}</td>
                <td className="fl-num">{totals.cr.toFixed(2)}%</td>
                <td className="fl-num col-epc">{totals.epc.toFixed(2)}</td>
                <td className="fl-num col-reg">{totals.registrations}</td>
                <td className="fl-num col-auto">{totals.auto_debit}</td>
                <td className="fl-num">{totals.net_purchases}</td>
                <td className="fl-num col-stars">{totals.tg_stars}</td>
                <td className="fl-num">{totals.income.toFixed(2)} ₽</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

export default ProjectIncomePage;